import { useKeyCloak } from '@/hooks/useKeyCloak'
import { useTranslation } from 'react-i18next'

import {
  ColorScheme,
  HelpIcon,
  KnowledgeBaseIcon,
  LogoutIcon,
  MailIcon,
  SendIcon,
  SettingsIcon,
  UserMenuItem,
} from '@hivewatch/dark'

import { trackEvent } from '@/utils/analytics'
import { SETTINGS_MANAGEMENT } from '@/utils/routes'

import {
  PROFILE_ANALYTICS,
  PROFILE_MENU_ICON_SIZE,
  SUPPORT_LINKS,
} from './constants'

interface UseProfileMenuItemsIProps {
  colorScheme: ColorScheme
  /** Opens the Send Feedback modal, which {@link SideBarProfile} owns. */
  onSendFeedback: () => void
}

/**
 * Builds the account menu's rows for `UserMenu`.
 *
 * Help is a nested submenu rather than a row that navigates — `UserMenu` owns
 * the disclosure, the "one submenu open at a time" rule and the click-outside
 * handling that the old `HelpPopover` hand-rolled with `getBoundingClientRect`
 * and four ref/callback props.
 */
export const useProfileMenuItems = ({
  colorScheme,
  onSendFeedback,
}: UseProfileMenuItemsIProps): UserMenuItem[] => {
  const { t } = useTranslation()
  const { logOut } = useKeyCloak()

  const iconProps = { colorScheme, size: PROFILE_MENU_ICON_SIZE }

  return [
    {
      icon: <SettingsIcon {...iconProps} />,
      label: t('common:settings'),
      onClick: () => trackEvent(PROFILE_ANALYTICS.settings),
      path: SETTINGS_MANAGEMENT,
      'data-testid': 'SideBarProfileMenu:settings',
    },
    {
      icon: <HelpIcon {...iconProps} />,
      label: t('common:help'),
      onClick: () => trackEvent(PROFILE_ANALYTICS.help),
      'data-testid': 'SideBarProfileMenu:help',
      submenu: [
        {
          icon: <SendIcon {...iconProps} />,
          // The menu stays open behind the modal, as it did before the
          // rebrand; SideBarProfile suppresses dismissal while it is up.
          keepOpen: true,
          label: t('common:help_send_feedback'),
          onClick: () => {
            trackEvent(PROFILE_ANALYTICS.sendFeedback)
            onSendFeedback()
          },
          'data-testid': 'HelpPopover:sendFeedback',
        },
        {
          href: SUPPORT_LINKS.EMAIL,
          icon: <MailIcon {...iconProps} />,
          label: t('common:help_email_us'),
          'data-testid': 'HelpPopover:emailUs',
        },
        {
          href: SUPPORT_LINKS.KNOWLEDGE_BASE,
          icon: <KnowledgeBaseIcon {...iconProps} />,
          label: t('common:help_knowledge_base'),
          'data-testid': 'HelpPopover:knowledgeBase',
        },
      ],
    },
    {
      icon: <LogoutIcon {...iconProps} />,
      label: t('common:logout'),
      onClick: () => {
        trackEvent(PROFILE_ANALYTICS.logout)
        logOut()
      },
      'data-testid': 'SideBarProfileMenu:logout',
    },
  ]
}
